/** ID proof number rules keyed by resolved document kind. */
export const ID_PROOF_RULES = Object.freeze({
  aadhaar: {
    regex: /^\d{12}$/,
    message: "Aadhaar Number must contain exactly 12 digits.",
  },
  pan: {
    regex: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    message: "Invalid PAN Number format.",
  },
  passport: {
    regex: /^[A-Za-z0-9]{6,9}$/,
    message: "Passport Number must be 6–9 alphanumeric characters.",
  },
  driving_license: {
    regex: /^[A-Za-z0-9]{5,20}$/,
    message: "Please enter a valid Driving License Number.",
  },
  voter_id: {
    regex: /^[A-Za-z0-9]{10}$/,
    message: "Voter ID must contain exactly 10 alphanumeric characters.",
  },
  ssn: {
    regex: /^\d{3}-\d{2}-\d{4}$/,
    message: "SSN must be in the format XXX-XX-XXXX.",
  },
  national_id: {
    regex: /^[A-Za-z0-9]{5,20}$/,
    message: "Please enter a valid National ID.",
  },
});

export const ID_PROOF_NUMBER_REQUIRED_MSG =
  "ID proof number is required when ID proof type is selected.";

export const ID_PROOF_TYPE_REQUIRED_MSG =
  "Select an ID proof type when entering an ID proof number.";

/** Map master `type_name` to validation kind (supports admin labels). */
export function resolveIdProofKind(typeName) {
  const n = String(typeName ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");

  if (n.includes("aadhaar")) return "aadhaar";
  if (n.includes("passport")) return "passport";
  if (/\bpan\b/.test(n) || n.startsWith("pan ")) return "pan";
  if (n.includes("driving") && n.includes("license")) return "driving_license";
  if (n.includes("voter")) return "voter_id";
  if (n.includes("ssn")) return "ssn";
  if (n.includes("national") && n.includes("id")) return "national_id";
  return null;
}

export function normalizeIdProofNumber(kind, raw) {
  const value = String(raw ?? "");
  switch (kind) {
    case "aadhaar":
      return value.replace(/\D/g, "").slice(0, 12);
    case "pan":
      return value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 10);
    case "passport":
      return value.replace(/[^A-Za-z0-9]/g, "").slice(0, 9);
    case "driving_license":
    case "national_id":
      return value.replace(/[^A-Za-z0-9]/g, "").slice(0, 20);
    case "voter_id":
      return value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 10);
    case "ssn": {
      const digits = value.replace(/\D/g, "").slice(0, 9);
      if (digits.length <= 3) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    }
    default:
      return value.trim();
  }
}

/** Returns a user-facing error message, or null when valid. */
export function validateIdProofNumber(typeName, rawNumber) {
  const trimmed = String(rawNumber ?? "").trim();
  if (!trimmed) return ID_PROOF_NUMBER_REQUIRED_MSG;

  const kind = resolveIdProofKind(typeName);
  if (!kind) {
    return "Selected ID proof type is not supported for validation.";
  }

  const normalized = normalizeIdProofNumber(kind, trimmed);
  const rule = ID_PROOF_RULES[kind];
  if (!rule?.regex.test(normalized)) {
    return rule?.message ?? "Please enter a valid ID proof number.";
  }
  return null;
}

export function validateIdProofPair({ typeId, number }) {
  const hasType = typeId != null && String(typeId).trim() !== "";
  const trimmed = String(number ?? "").trim();

  if (hasType && !trimmed) {
    return { field: "id_proof_number", message: ID_PROOF_NUMBER_REQUIRED_MSG };
  }
  if (!hasType && trimmed) {
    return { field: "id_proof_type_id", message: ID_PROOF_TYPE_REQUIRED_MSG };
  }
  return null;
}

export function idProofPlaceholder(typeName) {
  const kind = resolveIdProofKind(typeName);
  switch (kind) {
    case "aadhaar":
      return "12-digit Aadhaar number";
    case "pan":
      return "ABCDE1234F";
    case "passport":
      return "6–9 alphanumeric characters";
    case "driving_license":
      return "5–20 alphanumeric characters";
    case "voter_id":
      return "ABC1234567";
    case "ssn":
      return "123-45-6789";
    case "national_id":
      return "5–20 alphanumeric characters";
    default:
      return "Enter ID proof number";
  }
}
